import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { People, Add, Edit, Save, Cancel } from '@mui/icons-material';
import { useTranslation } from '../../../i18n';
import { BRAND_COLORS } from '../../../styles/theme';
import { ROLE_OPTIONS } from '../../../config/roles';
import { formatDateISO } from '../../../utils/dateUtils';
import type {
  AdminDialogProps,
  OrganizationUsersResponse,
  OrganizationUser,
} from '../types/adminTools.types';

interface UserManagementDialogProps extends AdminDialogProps {
  // Data
  data: OrganizationUsersResponse | null;
  loading: boolean;
  error: string;

  // Edit state
  editingUserId: string | null;
  editedRoles: string[];
  savingRoles: boolean;

  // Actions
  onStartEdit: (userId: string, currentRoles: string[]) => void;
  onCancelEdit: () => void;
  onToggleRole: (role: string) => void;
  onSaveRoles: (userId: string) => Promise<void>;

  // Invite dialog integration
  onOpenInviteDialog: () => void;

  // Organization info for header
  organizationName?: string;
}

export const UserManagementDialog: React.FC<UserManagementDialogProps> = ({
  open,
  onClose,
  data,
  loading,
  error,
  editingUserId,
  editedRoles,
  savingRoles,
  onStartEdit,
  onCancelEdit,
  onToggleRole,
  onSaveRoles,
  onOpenInviteDialog,
  organizationName,
}) => {
  const { t } = useTranslation();

  // Map ROLE_OPTIONS to include translated labels
  const roleDefinitions = ROLE_OPTIONS.map((role) => ({
    value: role.value,
    label: t(role.labelKey),
    description: t(role.descKey),
  }));

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'pilot':
        return 'primary';
      case 'maintenance':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth data-testid="user-management-dialog">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <People />
            {t('adminToolsPage.userManagement.title')}
            {organizationName && (
              <Chip label={organizationName} size="small" color="primary" sx={{ ml: 1 }} />
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onOpenInviteDialog}
            sx={{
              backgroundColor: BRAND_COLORS.forestGreen.main,
              '&:hover': {
                backgroundColor: BRAND_COLORS.forestGreen.main,
                opacity: 0.8,
              },
            }}
          >
            {t('adminToolsPage.userManagement.inviteUser')}
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : data ? (
          <Box sx={{ mt: 2 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('adminToolsPage.userManagement.tableHeaders.name')}</TableCell>
                    <TableCell>{t('adminToolsPage.userManagement.tableHeaders.email')}</TableCell>
                    <TableCell>{t('adminToolsPage.userManagement.tableHeaders.roles')}</TableCell>
                    <TableCell>{t('adminToolsPage.userManagement.tableHeaders.joined')}</TableCell>
                    <TableCell align="center">
                      {t('adminToolsPage.userManagement.tableHeaders.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.users.map((user: OrganizationUser) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.full_name || 'N/A'}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {editingUserId === user.id ? (
                          <FormGroup row>
                            {roleDefinitions.map((role) => (
                              <Tooltip key={role.value} title={role.description} placement="top">
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={editedRoles.includes(role.value)}
                                      onChange={() => onToggleRole(role.value)}
                                      disabled={savingRoles}
                                    />
                                  }
                                  label={<Typography variant="caption">{role.label}</Typography>}
                                  sx={{ mr: 1 }}
                                />
                              </Tooltip>
                            ))}
                          </FormGroup>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {user.roles && user.roles.length > 0 ? (
                              user.roles.map((role: string) => {
                                const roleDef = roleDefinitions.find((r) => r.value === role);
                                return (
                                  <Chip
                                    key={role}
                                    label={roleDef?.label || role}
                                    size="small"
                                    color={getRoleColor(role)}
                                  />
                                );
                              })
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                {t('adminToolsPage.roles.noRolesAssigned')}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>{formatDateISO(user.created_at)}</TableCell>
                      <TableCell align="center">
                        {editingUserId === user.id ? (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onSaveRoles(user.id)}
                              disabled={savingRoles || editedRoles.length === 0}
                            >
                              {savingRoles ? <CircularProgress size={16} /> : <Save />}
                            </IconButton>
                            <IconButton
                              size="small"
                              color="default"
                              onClick={onCancelEdit}
                              disabled={savingRoles}
                            >
                              <Cancel />
                            </IconButton>
                          </Box>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => onStartEdit(user.id, user.roles || [])}
                            disabled={editingUserId !== null}
                          >
                            <Edit />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {data.users.length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, textAlign: 'center' }}
              >
                {t('adminToolsPage.userManagement.noUsersFound')}
              </Typography>
            )}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('adminToolsPage.close')}</Button>
      </DialogActions>
    </Dialog>
  );
};
